"use client";

import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { IconBox } from "@/components/icon-box";
import { usePageTransition } from "@/components/page-transition-provider";
import "swiper/css";
import "swiper/css/navigation";
import Avatars from "~/public/assets/avatars.png";
import Bg_Section from "~/public/assets/backgrounds/bg-section-bottom.png";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";

type ProceduresServicesSectionProps = {
	service: Pick<ServicePageContent, "procedures" | "iconVariant">;
};

type ProcedureCardProps = {
	item: NonNullable<ServicePageContent["procedures"]>["items"][number];
	iconVariant?: ServicePageContent["iconVariant"];
};

function ProcedureCard({ item, iconVariant }: ProcedureCardProps) {
	return (
		<article
			data-service-card
			className="flex flex-col text-center items-center rounded-2xl bg-gray-900 p-6 pb-10 md:p-9 md:pb-16 w-full h-full"
		>
			{iconVariant === "outline" ? (
				<IconBox data-service-card-media bordered icon={item.icon} />
			) : (
				<Image
					data-service-card-media
					src={item.icon}
					alt=""
					aria-hidden="true"
					className="h-auto w-12 md:w-15"
					width={300}
					height={300}
				/>
			)}
			<div className="mt-8 flex flex-1 flex-col items-center">
				<h3 className="text-lg lg:text-xl font-medium">{item.title}</h3>
				<p className="mt-5 text-white/50 leading-[160%]">{item.description}</p>
			</div>
		</article>
	);
}

function ProceduresCta() {
	const { navigate } = usePageTransition();

	return (
		<a
			href="/solicitar-proposta"
			className="hover:text-primary-300/70 text-primary-300 flex gap-7 items-center"
			onClick={(event) => {
				event.preventDefault();

				void navigate("/solicitar-proposta", {
					origin: {
						x: event.clientX,
						y: event.clientY,
					},
				});
			}}
		>
			<span className="text-base xl:text-xl font-bricolage">
				Falar com a gente
			</span>
			<Image
				src={Avatars}
				alt="Equipe Flow"
				className="h-auto w-full max-w-38.75"
				width={1000}
				height={1000}
			/>
		</a>
	);
}

export default function ProceduresServicesSection({
	service,
}: ProceduresServicesSectionProps) {
	const procedures = service.procedures;
	const sectionRef = useRef<HTMLElement | null>(null);

	useServiceCardsAnimation(sectionRef, {
		preset: "drift",
	});

	if (!procedures) return null;

	return (
		<section
			ref={sectionRef}
			className="md:container-padding relative text-white"
		>
			<Image
				src={Bg_Section}
				alt="Background decorativo"
				className="pointer-events-none absolute bottom-0 h-full max-h-150 w-full scale-x-[-1] object-cover opacity-70"
				priority={false}
				sizes="100vw"
			/>

			<div className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-16 lg:py-24 lg:pb-210 xl:pb-160 3xl:pb-140!">
				<span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />
				<span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />

				<div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
					<div className="max-w-175">
						<p className="tag">{procedures.tag}</p>
						<h2 className="title max-w-150">{procedures.title}</h2>
					</div>

					<div className="md:pt-11">
						<ProceduresCta />
					</div>
				</div>
				<div data-service-cards-animation-trigger className="mt-16 lg:hidden">
					<Swiper
						a11y={{ enabled: true }}
						className="overflow-visible! [&_.swiper-wrapper]:items-stretch"
						modules={[Navigation, A11y]}
						navigation={{
							prevEl: ".procedures-prev",
							nextEl: ".procedures-next",
						}}
						slidesPerView="auto"
						spaceBetween={16}
					>
						{procedures.items.map((item) => (
							<SwiperSlide
								className="h-auto! max-w-[94%] sm:max-w-md md:max-w-lg"
								key={item.title}
							>
								<ProcedureCard item={item} iconVariant={service.iconVariant} />
							</SwiperSlide>
						))}
					</Swiper>

					<div className="mt-5 flex items-center gap-5">
						<button
							aria-label="Card anterior"
							className="procedures-prev cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowLeft
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
						<button
							aria-label="Próximo card"
							className="procedures-next cursor-pointer text-white transition-all duration-300 ease-in-out"
							type="button"
						>
							<ArrowRight
								className="size-9 text-primary-300"
								strokeWidth={1.5}
							/>
						</button>
					</div>
				</div>
			</div>

			<div
				data-service-cards-animation-trigger
				className="absolute bottom-24 z-20 hidden w-full gap-4 px-7 lg:grid lg:grid-cols-2 xl:grid-cols-4"
			>
				{procedures.items.map((item) => (
					<ProcedureCard
						key={item.title}
						item={item}
						iconVariant={service.iconVariant}
					/>
				))}
			</div>
		</section>
	);
}
